<template>
  <div
    class="h-full w-full bg-indigo-600 text-white flex items-center justify-between px-3 sm:px-6 shadow-sm select-none">
    <div class="flex items-center gap-2 sm:gap-3 shrink-0">
      <!-- Organization Logo (Shown on Dashboard) -->
      <router-link v-if="!canGoBack" to="/app/dashboard"
        class="flex items-center gap-2 text-white no-underline hover:opacity-90 shrink-0">
        <img :src="displayLogo" alt="Logo" class="h-8 max-w-[140px] object-contain rounded bg-white/10 px-1 py-0.5" />
      </router-link>

      <!-- Back button + Step Icon + Screen Title (Shown on all other screens in place of logo) -->
      <div v-else class="flex items-center gap-2 sm:gap-2.5 shrink-0">
        <!-- Back Button -->
        <button type="button" @click="handleBack"
          class="h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/35 text-white transition-colors cursor-pointer border-none p-0 shrink-0"
          title="Go Back">
          <v-icon small color="white">mdi-arrow-left</v-icon>
        </button>

        <!-- Step / Screen Icon Badge -->
        <!-- <div
          class="h-8 w-8 rounded-full bg-white/20 border border-white/20 flex items-center justify-center text-white shadow-xs shrink-0">
          <v-icon small color="white">{{ currentScreenInfo.icon }}</v-icon>
        </div> -->

        <!-- Screen Title & Subtitle -->
        <div class="min-w-0 flex flex-col justify-center">
          <h2
            class="text-xs sm:text-sm font-bold text-white tracking-tight leading-tight mb-0 truncate max-w-[180px] sm:max-w-xs">
            {{ currentScreenInfo.title }}
          </h2>
          <!-- <span v-if="currentScreenInfo.subtitle"
            class="text-[10px] sm:text-xs text-indigo-100 font-medium leading-none truncate max-w-[180px] sm:max-w-xs mt-0.5">
            {{ currentScreenInfo.subtitle }}
          </span> -->
        </div>
      </div>
    </div>

    <!-- Center/Right Section: Desktop Nav Links (Visible on md+ screens) -->
    <nav class="hidden md:flex items-center gap-1.5 lg:gap-2 ml-auto mr-3">
      <router-link v-for="link in links" :key="link.key || link.title" :to="link.to"
        class="px-3.5 py-1.5 rounded-xl text-xs lg:text-sm font-bold no-underline transition-all flex items-center gap-1.5 text-white"
        :class="isActive(link.to)
          ? 'bg-white/20 shadow-xs'
          : 'hover:bg-white/10'">
        <v-icon small color="white">{{ link.icon }}</v-icon>
        <span class="text-white">{{ link.title }}</span>
      </router-link>
    </nav>

    <!-- Notification Alerts Bell Button -->
    <v-btn icon color="white" class="hover:bg-indigo-500" @click="$emit('open-notifications')" v-if="!canGoBack">
      <v-badge :content="notificationsCount" :value="notificationsCount > 0" color="red" overlap small>
        <v-icon size="20">mdi-bell-outline</v-icon>
      </v-badge>
    </v-btn>

    <!-- Right Section (Avatar & Notifications) -->
    <div v-if="!canGoBack">
      <v-menu v-model="profileMenuOpen" offset-y left :close-on-content-click="true" transition="slide-y-transition"
        min-width="170" z-index="999">
        <template v-slot:activator="{ on, attrs }">
          <button type="button" v-bind="attrs" v-on="on"
            class="flex items-center gap-1 cursor-pointer py-1 px-1 rounded-full hover:bg-indigo-500 transition-colors focus:outline-none bg-transparent border-none">
            <v-avatar size="32" color="indigo-400" class="border border-white/60 shadow-xs">
              <img v-if="userAvatar && !avatarError" :src="userAvatar" alt="Avatar" @error="avatarError = true" />
              <v-icon v-else small color="white">mdi-account</v-icon>
            </v-avatar>
            <v-icon small color="white">mdi-chevron-down</v-icon>
          </button>
        </template>

        <v-list dense class="py-1 rounded-xl">
          <div class="px-4 py-2 border-b border-gray-100">
            <p class="font-bold text-gray-800 text-sm mb-0">{{ userName }}</p>
            <p class="text-xs text-gray-500 mb-0 truncate">{{ userEmailOrPhone }}</p>
          </div>

          <v-list-item @click="$emit('open-profile')">
            <v-list-item-icon class="mr-2">
              <v-icon small color="primary">mdi-account-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="text-xs font-semibold">My Profile</v-list-item-title>
          </v-list-item>

          <v-list-item @click="$emit('open-notifications')">
            <v-list-item-icon class="mr-2">
              <v-icon small color="indigo">mdi-bell-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="text-xs font-semibold">Alerts</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="orgQrCode" @click="showQrDialog = true">
            <v-list-item-icon class="mr-2">
              <v-icon small color="teal">mdi-qrcode-scan</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="text-xs font-semibold">Payment QR Code</v-list-item-title>
          </v-list-item>

          <v-divider class="my-1"></v-divider>

          <v-list-item @click="logout">
            <v-list-item-icon class="mr-2">
              <v-icon small color="error">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-title class="text-xs font-semibold text-red-600">Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Payment QR Dialog -->
    <v-dialog v-model="showQrDialog" max-width="360">
      <v-card class="rounded-2xl pa-4 text-center">
        <h3 class="text-base font-bold text-gray-900 mb-2">Organization Payment QR</h3>
        <p class="text-xs text-gray-500 mb-4">Scan QR code to receive customer payments</p>
        <img :src="orgQrCode" alt="QR" class="w-full max-w-[240px] mx-auto rounded-xl border border-gray-200 p-2" />
        <v-card-actions class="justify-end mt-4">
          <v-btn text :color="$vuetify.theme.currentTheme.employeePrimary" @click="showQrDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'AppTopNav',
  props: {
    links: {
      type: Array,
      default: () => []
    },
    userData: {
      type: Object,
      default: () => ({})
    },
    orgLogo: {
      type: String,
      default: ''
    },
    orgQrCode: {
      type: String,
      default: ''
    },
    notificationsCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      showQrDialog: false,
      profileMenuOpen: false,
      avatarError: false
    }
  },
  watch: {
    userAvatar() {
      this.avatarError = false
    }
  },
  computed: {
    effectiveUserData() {
      if (this.userData && Object.keys(this.userData).length > 0) {
        return this.userData
      }
      return this.$storage?.getUniversal('user') || JSON.parse(localStorage.getItem('user') || 'null') || {}
    },
    displayLogo() {
      if (this.orgLogo) return this.orgLogo
      try {
        return require('@/assets/img/CW_DEFAULT_WHITE.png')
      } catch (e) {
        return ''
      }
    },
    userName() {
      const u = this.effectiveUserData || {}
      const user = u.user || u
      const sysUser = (user.systemUserId && typeof user.systemUserId === 'object')
        ? user.systemUserId
        : (u.systemUserId && typeof u.systemUserId === 'object' ? u.systemUserId : {})
      const first = user.firstName || user.first_name || sysUser.firstName || sysUser.first_name || ''
      const last = user.lastName || user.last_name || sysUser.lastName || sysUser.last_name || ''
      const full = `${first} ${last}`.trim()
      return full || user.name || 'Employee'
    },
    userEmailOrPhone() {
      const u = this.effectiveUserData || {}
      const user = u.user || u
      const sysUser = (user.systemUserId && typeof user.systemUserId === 'object')
        ? user.systemUserId
        : (u.systemUserId && typeof u.systemUserId === 'object' ? u.systemUserId : {})
      return user.email || sysUser.email || user.mobile || sysUser.mobile || user.username || ''
    },
    userAvatar() {
      const u = this.effectiveUserData || {}
      const user = u.user || u
      const sysUser = (user.systemUserId && typeof user.systemUserId === 'object')
        ? user.systemUserId
        : (u.systemUserId && typeof u.systemUserId === 'object' ? u.systemUserId : {})

      // Check all potential profile image paths, prioritizing systemUserId.profileImage
      const img = sysUser.profileImage || sysUser.profile_image || user.profile_image || user.profileImage || u.profile_image || u.profileImage || ''
      if (!img || typeof img !== 'string') return ''

      if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('data:')) {
        return img
      }

      const s3Url = (process.env.VUE_APP_S3_BASE_URL || 'https://uat-cdn.myfinfi.com/').replace(/\/+$/, '')
      const cleanImg = img.replace(/^\/+/, '')
      return `${s3Url}/${cleanImg}`
    },
    currentScreenInfo() {
      const name = this.$route.name
      const screenMap = {
        AppDashboard: {
          title: 'Home Dashboard',
          subtitle: 'Tasks & Attendance',
          icon: 'mdi-view-dashboard'
        },
        AppTaskCreate: {
          title: 'Create New Task / Visit',
          subtitle: 'Step-by-step task creation',
          icon: 'mdi-plus-box-outline'
        },
        AppAttendance: {
          title: 'Attendance & Leaves',
          subtitle: 'Daily punches & monthly summary',
          icon: 'mdi-calendar-clock'
        },
        AppClaim: {
          title: 'Expenses & Claims',
          subtitle: 'Corporate reimbursement claims',
          icon: 'mdi-cash-refund'
        },
        AppProfile: {
          title: 'My Profile',
          subtitle: 'Employee personal details',
          icon: 'mdi-account-circle'
        },
        AppStartJob: {
          title: 'Start Task',
          subtitle: 'Customer & case info',
          icon: 'mdi-map-marker-path'
        },
        AppTaskQuestionnaire: {
          title: 'Task Questionnaire',
          subtitle: 'Step • Mandatory Questions',
          icon: 'mdi-clipboard-text-outline'
        },
        AppTaskSelfie: {
          title: 'Visit Verification',
          subtitle: 'Step • Camera Selfie',
          icon: 'mdi-camera-outline'
        },
        AppTaskAudio: {
          title: 'Voice Recording',
          subtitle: 'Step • Audio Note',
          icon: 'mdi-microphone-outline'
        },
        AppTaskMeeting: {
          title: 'Meeting Notes',
          subtitle: 'Step • Outcome & Remarks',
          icon: 'mdi-note-text-outline'
        },
        AppTaskCompleted: {
          title: 'Task Completed',
          subtitle: 'All steps verified',
          icon: 'mdi-check-decagram'
        }
      }
      return screenMap[name] || {
        title: this.$route.meta?.title || 'CualityWork',
        subtitle: '',
        icon: 'mdi-cellphone-link'
      }
    },
    canGoBack() {
      return this.$route.name !== 'AppDashboard'
    }
  },
  methods: {
    handleBack() {
      const name = this.$route.name
      if (name === 'AppStartJob' || name === 'AppTaskCompleted' || name === 'AppAttendance' || name === 'AppProfile') {
        this.$router.push('/app/dashboard')
      } else if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/app/dashboard')
      }
    },
    isActive(routePath) {
      return this.$route.path === routePath
    },
    logout() {
      this.$axios
        .post('/auth/v3/logout')
        .catch(() => { })
        .finally(() => {
          localStorage.clear()
          localStorage.setItem('theme', 'light')
          this.$storage?.removeUniversal('isAuthenticated')
          this.$storage?.removeUniversal('user')
          this.$router.replace('/login')
        })
    }
  }
}
</script>
